import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    buildIRLEvidenceAskScope,
    buildIRLEvidenceValidationPrompt,
} from '../utils/buildIRLEvidenceValidationPrompt';
import { htmlToPlainText } from '../utils/commonUtils';
import { dispatchScopedRagAsk } from '../utils/ragDispatch';

/**
 * Single intelligence ask with primary_irl_id — backend runs VDR-first validation
 * (same retrieval + verdict as two-pass, ~half the latency).
 */
export function useIRLEvidenceAsk({ irlRow, workstreamName, projectName }) {
    const dispatch = useDispatch();
    const { selectedProject } = useSelector(state => state.projectData);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);

    const runEvidenceValidation = useCallback(async () => {
        if (!irlRow?.irresponse) {
            setError('A management response is required before validating evidence.');
            return null;
        }

        const projectId = selectedProject?.id || localStorage.getItem('project_id');
        if (!projectId) {
            setError('No project selected.');
            return null;
        }

        const irlRef = [irlRow?.prefix, irlRow?.item, irlRow?.sub_item].filter(v => v != null && v !== '').join('.');
        const question = buildIRLEvidenceValidationPrompt(irlRow, {
            workstreamName,
            projectName: projectName || selectedProject?.name,
            irlRef,
        });

        const scope = buildIRLEvidenceAskScope(irlRow);
        if (!scope.workstreamIds?.length) {
            setError('Workstream is missing for this IRL item.');
            return null;
        }

        const irlQuestion = htmlToPlainText(irlRow?.request || '').trim();
        const ragFallbackBody = {
            question: irlQuestion
                ? `Find data room evidence to validate this management claim: ${irlQuestion}`
                : question,
            top: 24,
        };
        if (scope.workstreamIds?.length) ragFallbackBody.workstream_ids = scope.workstreamIds;
        if (scope.fileIds?.length) ragFallbackBody.file_ids = scope.fileIds;

        setLoading(true);
        setError(null);
        setResult(null);

        try {
            const { data } = await dispatchScopedRagAsk({
                dispatch,
                projectId,
                wsId: scope.wsId,
                scope,
                body: { question },
                ragFallbackBody,
                preferIntelligence: true,
            });

            setResult(data);
            return data;
        } catch (e) {
            const msg = e?.detail || e?.error || (typeof e === 'string' ? e : 'Evidence validation failed');
            setError(typeof msg === 'string' ? msg : 'Evidence validation failed');
            return null;
        } finally {
            setLoading(false);
        }
    }, [dispatch, irlRow, workstreamName, projectName, selectedProject]);

    const reset = useCallback(() => {
        setResult(null);
        setError(null);
    }, []);

    return {
        loading,
        error,
        result,
        runEvidenceValidation,
        reset,
    };
}

export default useIRLEvidenceAsk;
